import { LockKeyhole, Pencil, Pin, PinOff, RefreshCw, Shield } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { defaultPasswordPolicy, generatePassword, type PasswordPolicy } from '../../shared/crypto';
import type { LocalRecord, RecordData, VaultItem } from '../../shared/model';
import type { ProjectChoice } from '../../shared/productivity';
import { encryptVaultExport } from '../../shared/vault-export';

import { request, workspace } from '../lib/engine';
import { download } from '../lib/files';
import {
  ActionIconButton,
  Button,
  Disclosure,
  IconButton,
  Input,
  MobileDetails,
  Modal,
  PageHeader,
  SearchField,
  Select,
} from './ui';
import { VaultAttachment } from './VaultAttachment';
import { VaultFields, VaultTypeIcon, vaultTypes } from './VaultFields';

export function VaultLab({
  records,
  save,
  reviewConflict,
}: {
  records: LocalRecord[];
  save: (id: string, value: VaultItem) => Promise<void>;
  /** Opens the workspace's version comparison on this entry. */
  reviewConflict: (id: string) => void;
}) {
  const sync = useSyncExternalStore(workspace.subscribe, workspace.snapshot);
  const [closePrompt, setClosePrompt] = useState(false);

  const [editing, setEditing] = useState(false),
    [saving, setSaving] = useState(false);

  const locked = !editing || saving;

  const [selected, setSelected] = useState<string | null>(null),
    [trash, setTrash] = useState(false),
    [message, setMessage] = useState('');

  const [draft, setDraft] = useState<VaultItem | null>(null),
    [exporting, setExporting] = useState(false),
    [exportPassword, setExportPassword] = useState('');

  const [search, setSearch] = useState(''),
    [length, setLength] = useState(24),
    [policy, setPolicy] = useState<PasswordPolicy>(defaultPasswordPolicy),
    [history, setHistory] = useState<RecordData[]>(),
    [purging, setPurging] = useState(false),
    [revealedRevision, setRevealedRevision] = useState<number>();

  const [projects, setProjects] = useState<ProjectChoice[]>([]),
    [projectStatus, setProjectStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const [projectRefresh, setProjectRefresh] = useState(0);

  useEffect(() => {
    let active = true,
      sequence = 0;

    const load = () => {
      const current = ++sequence;

      setProjectStatus('loading');

      void request<ProjectChoice[]>('/api/vault/projects')
        .then(rows => {
          if (active && current === sequence) {
            setProjects(rows);
            setProjectStatus('ready');
          }
        })
        .catch(() => {
          if (active && current === sequence) {
            setProjectStatus('error');
          }
        });
    };

    load();
    window.addEventListener('focus', load);
    window.addEventListener('online', load);

    return () => {
      active = false;
      window.removeEventListener('focus', load);
      window.removeEventListener('online', load);
    };
  }, [selected, projectRefresh]);

  const projectTitle = (id?: string | null) =>
    id
      ? (projects.find(project => project.id === id)?.title ?? 'Linked project (unavailable)')
      : '';

  const items = records.filter(
    record =>
      Boolean((record.value as VaultItem).trashed) === trash &&
      !record.deleted &&
      `${JSON.stringify(record.value)} ${projectTitle((record.value as VaultItem).projectId)}`
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()),
  );

  const pinned = (record: LocalRecord) => Boolean((record.value as VaultItem).pinned);

  items.sort((a, b) => Number(pinned(b)) - Number(pinned(a)));

  async function togglePinned(record: LocalRecord) {
    const value = record.value as VaultItem;

    setSaving(true);

    try {
      await save(record.id, { ...value, pinned: !value.pinned });
      setMessage(value.pinned ? 'Unpinned.' : 'Pinned to the top of the list.');
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!editing && selected) {
      const current = records.find(record => record.id === selected && !record.deleted);

      setDraft(current ? structuredClone(current.value as VaultItem) : null);
    }
  }, [records, selected, editing]);

  function cancelEditing() {
    setClosePrompt(false);
    const current = records.find(record => record.id === selected && !record.deleted);

    setDraft(current ? structuredClone(current.value as VaultItem) : null);

    if (!current) {
      setSelected(null);
    }

    setEditing(false);
    setMessage('');
  }

  function closeDetails() {
    if (saving) {
      return;
    }

    const saved = records.find(record => record.id === selected)?.value;

    if (editing && JSON.stringify(draft) !== JSON.stringify(saved)) {
      setClosePrompt(true);

      return;
    }

    setSelected(null);
    setDraft(null);
    setEditing(false);
    setClosePrompt(false);
    setMessage('');
  }

  async function saveDraft() {
    if (locked || !selected || !draft) {
      return;
    }

    setSaving(true);

    try {
      await save(selected, draft);
      setClosePrompt(false);
      setEditing(false);

      setMessage(
        draft.file
          ? 'Entry and attachment saved on this device. Check Synced above before closing.'
          : 'Entry saved locally.',
      );
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setMessage('Copied to clipboard.');
    } catch {
      setMessage('Clipboard access was denied.');
    }
  }

  async function exportVault() {
    if (exportPassword.length < 12) {
      setMessage('Use an export password of at least 12 characters.');

      return;
    }

    const envelope = await encryptVaultExport(
      {
        records: records.filter(r => !r.deleted),
        includes: 'Current active entries and trash; server revision history is not included.',
        exportedAt: new Date().toISOString(),
      },
      exportPassword,
    );

    download('cairn-vault.encrypted.json', JSON.stringify(envelope, null, 2));
    setExportPassword('');
    setExporting(false);
    setMessage('Encrypted export downloaded. Keep its password separately.');
  }

  function startEntry() {
    setEditing(true);
    setMessage('');
    setSelected(crypto.randomUUID());
    setHistory(undefined);
    setRevealedRevision(undefined);

    setDraft({
      type: 'login',
      projectId: null,
      title: '',
      username: '',
      password: '',
      url: '',
      notes: '',
      trashed: false,
    });
  }

  return (
    <div className="vault-page">
      <PageHeader
        eyebrow="TEST DATA ONLY"
        title="Vault"
        actions={
          <ActionIconButton
            action="vault"
            label="New entry"
            disabled={saving}
            onClick={startEntry}
          />
        }
      />
      <SearchField label="Search vault" value={search} onChange={setSearch} />
      <div className="vault-toolbar">
        <div className="segmented">
          <Button
            aria-pressed={!trash}
            variant="ghost"
            className={!trash ? 'selected' : ''}
            onClick={() => setTrash(false)}
          >
            Entries
          </Button>
          <Button
            aria-pressed={trash}
            variant="ghost"
            className={trash ? 'selected' : ''}
            onClick={() => setTrash(true)}
          >
            Trash
          </Button>
        </div>
        <Button variant="ghost" onClick={() => setExporting(true)}>
          Encrypted export
        </Button>
        {trash && (
          <ActionIconButton
            action="delete"
            label="Empty trash"
            disabled={!items.length}
            onClick={() => setPurging(true)}
          />
        )}
      </div>
      {message && !(draft && selected) && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      {purging && (
        <Modal open onClose={() => setPurging(false)} title="Permanently empty vault trash?">
          <p>
            All {records.filter(r => !r.deleted && (r.value as VaultItem).trashed).length} trashed
            entries and their live server revision history will be removed. Offline deletion remains
            pending until sync. Old backups and other offline devices may retain copies until their
            retention or next synchronization.
          </p>
          <div className="button-row">
            <Button
              variant="danger"
              onClick={async () => {
                try {
                  await workspace.purgeVault(
                    records
                      .filter(r => !r.deleted && (r.value as VaultItem).trashed)
                      .map(r => r.id),
                  );

                  setPurging(false);
                  setDraft(null);
                  setMessage('Deletion saved locally. Check synchronization before closing.');
                } catch (e) {
                  setMessage((e as Error).message);
                }
              }}
            >
              Permanently empty trash
            </Button>
            <Button variant="secondary" onClick={() => setPurging(false)}>
              Cancel
            </Button>
          </div>
        </Modal>
      )}
      {exporting && (
        <form
          className="export-form"
          onSubmit={e => {
            e.preventDefault();
            void exportVault().catch(e => setMessage(e.message));
          }}
        >
          <label>
            Protect the exported file
            <Input
              type="password"
              minLength={12}
              required
              placeholder="Export password · 12+ characters"
              value={exportPassword}
              onChange={e => setExportPassword(e.target.value)}
            />
          </label>
          <div className="button-row">
            <Button variant="primary">Download encrypted file</Button>
            <Button variant="secondary" type="button" onClick={() => setExporting(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      <div className="vault-grid">
        <div className="vault-list">
          {items.length ? (
            items.map(record => (
              <div className="vault-row" key={record.id}>
                <button
                  type="button"
                  data-ui="specialized"
                  disabled={saving}
                  className={selected === record.id ? 'selected' : ''}
                  onClick={() => {
                    setEditing(false);
                    setMessage('');
                    setSelected(record.id);
                    setHistory(undefined);
                    setRevealedRevision(undefined);
                    setDraft(structuredClone(record.value as VaultItem));
                  }}
                >
                  <VaultTypeIcon type={(record.value as VaultItem).type} />
                  <span>
                    <strong>
                      {(record.value as VaultItem).projectId
                        ? `${projectTitle((record.value as VaultItem).projectId)} :: `
                        : ''}
                      {(record.value as VaultItem).title}
                    </strong>
                    <small>{vaultTypes[(record.value as VaultItem).type ?? 'login'].label}</small>
                  </span>
                </button>
                <IconButton
                  className={pinned(record) ? 'active' : ''}
                  aria-pressed={pinned(record)}
                  label={
                    pinned(record)
                      ? `Unpin ${(record.value as VaultItem).title}`
                      : `Pin ${(record.value as VaultItem).title}`
                  }
                  disabled={saving || (editing && selected === record.id)}
                  onClick={() => void togglePinned(record)}
                >
                  {pinned(record) ? <PinOff aria-hidden="true" /> : <Pin aria-hidden="true" />}
                </IconButton>
              </div>
            ))
          ) : (
            <div className="empty-state small">
              <Shield size={25} />
              <h3>{trash ? 'Trash is empty' : 'Start with a test entry'}</h3>
              <p>
                {trash
                  ? 'Trashed entries stay here until you explicitly empty the trash.'
                  : 'Generate a random password and test an offline edit.'}
              </p>
              {!trash && (
                <ActionIconButton
                  action="vault"
                  label="New entry"
                  disabled={saving}
                  onClick={startEntry}
                />
              )}
            </div>
          )}
        </div>
        {draft && selected ? (
          <MobileDetails
            open
            title="Vault entry"
            closeLabel="Close vault entry"
            onClose={closeDetails}
            busy={saving}
            status={
              sync.error ||
              sync.notice ||
              (sync.saving
                ? 'Saving locally…'
                : sync.offline
                  ? `${sync.pending} pending · offline`
                  : sync.syncing
                    ? 'Syncing…'
                    : sync.pending
                      ? `${sync.pending} pending`
                      : 'Synced')
            }
            prompt={
              closePrompt && (
                <div className="ui-mobile-details-prompt" role="alert">
                  <p>Discard unsaved changes?</p>
                  <div className="button-row">
                    <Button type="button" onClick={() => setClosePrompt(false)}>
                      Keep editing
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => {
                        setClosePrompt(false);
                        setSelected(null);
                        setDraft(null);
                        setEditing(false);
                        setMessage('');
                      }}
                    >
                      Discard changes
                    </Button>
                  </div>
                </div>
              )
            }
          >
            <form
              className="vault-editor"
              onSubmit={e => {
                e.preventDefault();
                void saveDraft();
              }}
            >
              {message && (
                <p className="notice" role="status">
                  {message}
                </p>
              )}
              <div className="section-heading">
                <h2>{editing ? 'Edit entry' : 'Entry details'}</h2>
                {!editing && (
                  <span className="vault-lock-status">
                    <LockKeyhole aria-hidden="true" size={16} />
                    Locked
                  </span>
                )}
                {!editing && (
                  <IconButton
                    label="Edit entry"
                    onClick={() => {
                      setEditing(true);
                      setMessage('');
                    }}
                  >
                    <Pencil aria-hidden="true" />
                  </IconButton>
                )}
              </div>
              {records.find(r => r.id === selected)?.competing ? (
                <div className="notice conflict-notice" role="status">
                  <p>
                    <strong>This entry was also changed on another device.</strong> Your edit is
                    saved here and waits until you choose which version to keep.
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => selected && reviewConflict(selected)}
                  >
                    Compare versions
                  </Button>
                </div>
              ) : (
                records.find(r => r.id === selected)?.conflict && (
                  <p className="notice">
                    Another device saved this entry at the same time, and an older Cairn replaced
                    its version with this one. The replaced version is in revision history below.
                  </p>
                )
              )}
              <label>
                Entry type
                <Select
                  disabled={locked}
                  value={draft.type ?? 'login'}
                  onChange={e => setDraft({ ...draft, type: e.target.value as VaultItem['type'] })}
                >
                  {Object.entries(vaultTypes).map(([value, meta]) => (
                    <option key={value} value={value}>
                      {meta.label}
                    </option>
                  ))}
                </Select>
              </label>
              <label>
                Title
                <Input
                  disabled={locked}
                  value={draft.title}
                  onChange={e => setDraft({ ...draft, title: e.target.value })}
                  required
                  placeholder="Example account"
                />
              </label>
              <div className="button-row field-actions vault-project-field">
                <label>
                  Project (optional)
                  <Select
                    disabled={locked}
                    value={draft.projectId ?? ''}
                    onChange={e => setDraft({ ...draft, projectId: e.target.value || null })}
                  >
                    <option value="">No project</option>
                    {draft.projectId &&
                      !projects.some(project => project.id === draft.projectId) && (
                        <option value={draft.projectId}>{projectTitle(draft.projectId)}</option>
                      )}
                    {projects
                      .filter(project => !project.nested || project.id === draft.projectId)
                      .map(project => (
                        <option key={project.id} value={project.id}>
                          {project.title}
                        </option>
                      ))}
                  </Select>
                </label>
                <IconButton
                  label="Refresh projects"
                  type="button"
                  disabled={projectStatus === 'loading'}
                  onClick={() => setProjectRefresh(value => value + 1)}
                >
                  <RefreshCw aria-hidden="true" />
                </IconButton>
              </div>
              {projectStatus === 'error' && (
                <p className="muted" role="status">
                  Projects could not be refreshed. Your current selection is kept; connect to
                  refresh the list.
                </p>
              )}
              {projectStatus === 'ready' && !projects.length && (
                <p className="muted">Create a project in your PARA Projects to link it here.</p>
              )}
              <VaultFields
                key={`${selected}:${draft.type}:${editing}`}
                draft={draft}
                locked={locked}
                onChange={patch =>
                  setDraft(current => (current ? { ...current, ...patch } : current))
                }
                copy={value => void copy(value)}
              />
              {editing && ['login', 'development'].includes(draft.type ?? 'login') && (
                <>
                  <Disclosure className="vault-generator" summary="Password generator">
                    <label>
                      Password length
                      <Input
                        disabled={saving}
                        type="number"
                        min={16}
                        max={128}
                        value={length}
                        onChange={e => setLength(Number(e.target.value))}
                      />
                    </label>
                    {(Object.keys(policy) as (keyof PasswordPolicy)[]).map(key => (
                      <label className="inline-check" key={key}>
                        <Input
                          disabled={saving}
                          type="checkbox"
                          checked={policy[key]}
                          onChange={e => setPolicy({ ...policy, [key]: e.target.checked })}
                        />
                        {key === 'excludeAmbiguous' ? 'Exclude ambiguous characters' : key}
                      </label>
                    ))}
                  </Disclosure>
                  <Button
                    disabled={saving}
                    variant="ghost"
                    type="button"
                    onClick={() => {
                      try {
                        setDraft({ ...draft, password: generatePassword(length, policy) });
                      } catch (e) {
                        setMessage((e as Error).message);
                      }
                    }}
                  >
                    Generate random password
                  </Button>
                </>
              )}
              <VaultAttachment
                key={selected}
                file={draft.file}
                editing={editing}
                busy={saving}
                onBusy={setSaving}
                onMessage={setMessage}
                onChange={file =>
                  setDraft(current =>
                    current
                      ? { ...current, file, title: current.title || file?.name || '' }
                      : current,
                  )
                }
              />
              <Button
                variant="ghost"
                type="button"
                onClick={async () => {
                  try {
                    setHistory(await request<RecordData[]>(`/api/history/${selected}`));
                  } catch (e) {
                    setMessage((e as Error).message);
                  }
                }}
              >
                Revision history
              </Button>
              {history && (
                <div className="history-panel">
                  {history.length ? (
                    history.map(row => (
                      <Disclosure
                        key={row.revision}
                        summary={`Revision ${row.revision} · ${new Date(row.updatedAt).toLocaleString()}`}
                      >
                        <p>{(row.value as VaultItem).title}</p>
                        <Button
                          variant="ghost"
                          type="button"
                          onClick={() =>
                            setRevealedRevision(
                              revealedRevision === row.revision ? undefined : row.revision,
                            )
                          }
                        >
                          {revealedRevision === row.revision
                            ? 'Hide revision contents'
                            : 'Reveal revision contents'}
                        </Button>
                        {revealedRevision === row.revision && (
                          <pre>{JSON.stringify(row.value, null, 2)}</pre>
                        )}
                        <ActionIconButton
                          action="restore"
                          label="Restore this revision"
                          type="button"
                          disabled={locked}
                          onClick={async () => {
                            try {
                              setSaving(true);
                              await save(selected, row.value as VaultItem);
                              setEditing(false);
                              setDraft(row.value as VaultItem);
                              setHistory(undefined);
                              setRevealedRevision(undefined);
                              setMessage('Restored as a new revision.');
                            } catch (e) {
                              setMessage((e as Error).message);
                            } finally {
                              setSaving(false);
                            }
                          }}
                        />
                      </Disclosure>
                    ))
                  ) : (
                    <p>No earlier server revisions.</p>
                  )}
                </div>
              )}
              <div className="button-row">
                {editing && (
                  <>
                    <Button variant="primary" disabled={saving}>
                      Save entry
                    </Button>
                    <Button type="button" disabled={saving} onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </>
                )}
                <ActionIconButton
                  disabled={locked}
                  action={draft.trashed ? 'restore' : 'trash'}
                  label={draft.trashed ? 'Restore entry' : 'Move entry to trash'}
                  type="button"
                  onClick={() => {
                    setSaving(true);
                    const value = { ...draft, trashed: !draft.trashed };

                    void save(selected, value)
                      .then(() => {
                        setSelected(null);
                        setDraft(null);
                        setEditing(false);

                        setMessage(
                          value.trashed ? 'Moved to trash. It can be restored.' : 'Entry restored.',
                        );
                      })
                      .catch(e => setMessage(e.message))
                      .finally(() => setSaving(false));
                  }}
                />
              </div>
            </form>
          </MobileDetails>
        ) : (
          <div className="vault-placeholder">
            <Shield size={36} />
            <p>Select a test entry to inspect it.</p>
          </div>
        )}
      </div>
    </div>
  );
}
